"use client";

import { useState } from "react";

import { ImageLightbox } from "@/components/ui/image-lightbox";

interface Props {
  /** All images for this listing, in display order. First is the hero. */
  images: readonly string[];
  /** Alt text used for the hero image (defaults to empty for decorative use). */
  alt?: string;
}

/**
 * Public-facing listing gallery: full-width hero on top, responsive thumbnail
 * grid below for the remaining images. Clicking ANY image opens the platform
 * lightbox at that index — never a new tab. This is the default for every
 * listing surface; do not reintroduce `<a target="_blank">` patterns.
 */
export function ListingGallery({ images, alt = "" }: Props) {
  const [open, setOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  if (images.length === 0) return null;

  const lightboxImages = images.map((url, i) => ({ id: String(i), url }));
  const hero = images[0]!;
  const rest = images.slice(1);

  const openAt = (i: number) => {
    setStartIndex(i);
    setOpen(true);
  };

  return (
    <>
      <div className="mt-8 overflow-hidden rounded-3xl bg-cream-100">
        <button
          type="button"
          onClick={() => openAt(0)}
          aria-label="Abrir galería"
          className="block w-full"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={hero}
            alt={alt}
            loading="eager"
            className="h-auto w-full cursor-zoom-in object-cover"
          />
        </button>
      </div>

      {rest.length > 0 && (
        <section
          aria-label="Galería de fotos"
          className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4"
        >
          {rest.map((src, idx) => {
            const lightboxIndex = idx + 1;
            return (
              <button
                key={lightboxIndex}
                type="button"
                onClick={() => openAt(lightboxIndex)}
                aria-label={`Ver imagen ${lightboxIndex + 1}`}
                className="group overflow-hidden rounded-2xl bg-cream-100 ring-1 ring-ink-200 transition hover:ring-coral"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  className="aspect-[4/3] h-full w-full cursor-zoom-in object-cover transition group-hover:scale-[1.02]"
                />
              </button>
            );
          })}
        </section>
      )}

      <ImageLightbox
        images={lightboxImages}
        startIndex={startIndex}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
